<template>
  <div class="product-cert-page">
    <!-- ===================== Hero Header ===================== -->
    <div class="pc-hero">
      <div class="pc-hero__blob pc-hero__blob--one" />
      <div class="pc-hero__blob pc-hero__blob--two" />

      <div class="pc-hero__content">
        <div class="pc-hero__icon">
          <feather-icon
            icon="PackageIcon"
            size="30"
          />
        </div>
        <div>
          <h2 class="pc-hero__title">
            Products &amp; Their Certificates
          </h2>
          <p class="pc-hero__subtitle">
            Browse every product category and the certificates required to bring it to market.
          </p>
        </div>
      </div>

      <button
        type="button"
        class="pc-back-btn"
        @click="goBack"
      >
        <span class="pc-back-btn__icon">
          <feather-icon
            icon="ArrowLeftIcon"
            size="16"
          />
        </span>
        <span class="text-nowrap">Back</span>
      </button>
    </div>

    <!-- ===================== Stats Header ===================== -->
    <b-row class="match-height mb-1">
      <b-col
        cols="12"
        sm="6"
        lg="3"
      >
        <b-card
          no-body
          class="stat-card stat-card--warning"
        >
          <b-card-body class="d-flex justify-content-between align-items-center">
            <div>
              <h2 class="font-weight-bolder mb-0 text-white">
                {{ stats.totalProducts }}
              </h2>
              <span class="text-white-75">Product Categories</span>
            </div>
            <b-avatar
              size="52"
              class="stat-avatar"
            >
              <feather-icon
                icon="PackageIcon"
                size="26"
              />
            </b-avatar>
          </b-card-body>
          <feather-icon
            icon="PackageIcon"
            size="90"
            class="stat-card__ghost-icon"
          />
        </b-card>
      </b-col>

      <b-col
        cols="12"
        sm="6"
        lg="3"
      >
        <b-card
          no-body
          class="stat-card stat-card--primary"
        >
          <b-card-body class="d-flex justify-content-between align-items-center">
            <div>
              <h2 class="font-weight-bolder mb-0 text-white">
                {{ stats.pageProducts }}
              </h2>
              <span class="text-white-75">On This Page</span>
            </div>
            <b-avatar
              size="52"
              class="stat-avatar"
            >
              <feather-icon
                icon="GridIcon"
                size="26"
              />
            </b-avatar>
          </b-card-body>
          <feather-icon
            icon="GridIcon"
            size="90"
            class="stat-card__ghost-icon"
          />
        </b-card>
      </b-col>

      <b-col
        cols="12"
        sm="6"
        lg="3"
      >
        <b-card
          no-body
          class="stat-card stat-card--info"
        >
          <b-card-body class="d-flex justify-content-between align-items-center">
            <div>
              <h2 class="font-weight-bolder mb-0 text-white">
                {{ stats.totalCertificates }}
              </h2>
              <span class="text-white-75">Linked Certificates</span>
            </div>
            <b-avatar
              size="52"
              class="stat-avatar"
            >
              <feather-icon
                icon="AwardIcon"
                size="26"
              />
            </b-avatar>
          </b-card-body>
          <feather-icon
            icon="AwardIcon"
            size="90"
            class="stat-card__ghost-icon"
          />
        </b-card>
      </b-col>

      <b-col
        cols="12"
        sm="6"
        lg="3"
      >
        <b-card
          no-body
          class="stat-card stat-card--success"
        >
          <b-card-body class="d-flex justify-content-between align-items-center">
            <div>
              <h2 class="font-weight-bolder mb-0 text-white">
                {{ stats.totalGroups }}
              </h2>
              <span class="text-white-75">Certificate Groups</span>
            </div>
            <b-avatar
              size="52"
              class="stat-avatar"
            >
              <feather-icon
                icon="LayersIcon"
                size="26"
              />
            </b-avatar>
          </b-card-body>
          <feather-icon
            icon="LayersIcon"
            size="90"
            class="stat-card__ghost-icon"
          />
        </b-card>
      </b-col>
    </b-row>

    <!-- ===================== Toolbar ===================== -->
    <b-card
      no-body
      class="pc-toolbar-card mb-1"
    >
      <div class="pc-toolbar">
        <div class="pc-toolbar__title">
          <b-avatar
            rounded
            variant="light-warning"
            size="40"
            class="mr-1"
          >
            <feather-icon
              icon="ListIcon"
              size="20"
            />
          </b-avatar>
          <div>
            <h4 class="mb-0 font-weight-bolder">
              Product Categories
            </h4>
            <small class="text-muted">Every category and the certificates it requires</small>
          </div>
        </div>

        <div class="pc-toolbar__actions">
          <b-input-group class="pc-search">
            <b-input-group-prepend is-text>
              <feather-icon icon="SearchIcon" />
            </b-input-group-prepend>
            <b-form-input
              v-model="searchQuery"
              placeholder="Search product categories... (press Enter)"
              autocomplete="off"
              @keyup.enter="searchNow"
            />
            <b-input-group-append
              v-if="searchQuery"
              is-text
              class="pc-search__clear"
              @click="clearSearch"
            >
              <feather-icon icon="XIcon" />
            </b-input-group-append>
          </b-input-group>
        </div>
      </div>
    </b-card>

    <!-- ===================== Loading ===================== -->
    <b-row v-if="dataLoad">
      <b-col
        v-for="n in 6"
        :key="n"
        cols="12"
        md="6"
        xl="4"
      >
        <div class="product-skel-card">
          <div class="product-skel-accent" />
          <div class="product-skel-head">
            <div class="product-skel-avatar" />
            <div class="product-skel-lines">
              <div class="product-skel-line product-skel-line--short" />
              <div class="product-skel-line product-skel-line--long" />
            </div>
          </div>
          <div class="product-skel-body">
            <div class="product-skel-chip" />
            <div class="product-skel-chip" />
            <div class="product-skel-chip" />
          </div>
        </div>
      </b-col>
    </b-row>

    <!-- ===================== Empty state ===================== -->
    <b-card
      v-else-if="!rows.length"
      class="text-center py-5"
    >
      <feather-icon
        icon="InboxIcon"
        size="48"
        class="text-muted mb-1"
      />
      <h4>No product categories found</h4>
      <p class="text-muted mb-0">
        {{ searchQuery ? 'Try adjusting your search.' : 'There is no data to display yet.' }}
      </p>
    </b-card>

    <!-- ===================== Product cards grid ===================== -->
    <b-row
      v-else
      class="products-grid"
    >
      <b-col
        v-for="row in rows"
        :key="row.id"
        cols="12"
        md="6"
        xl="4"
      >
        <div class="product-card">
          <div
            class="product-accent"
            :style="{ background: getProductGradient(row) }"
          />

          <!-- Header -->
          <div class="product-card-head">
            <div
              class="product-avatar"
              :style="{ background: getProductGradient(row) }"
            >
              <feather-icon
                :icon="getProductIcon(row)"
                size="22"
              />
            </div>
            <div class="product-head-text">
              <h4
                class="product-name"
                :title="row.name"
              >
                {{ row.name }}
              </h4>
              <span class="product-id">#{{ row.id }}</span>
            </div>

            <b-badge
              pill
              variant="light-warning"
              class="product-count-badge"
            >
              {{ (row.certificates || []).length }}
              certificate{{ (row.certificates || []).length === 1 ? '' : 's' }}
            </b-badge>
          </div>

          <!-- Certificates -->
          <div class="product-card-body">
            <div
              v-if="row.certificates && row.certificates.length"
              class="cert-list"
            >
              <div
                v-for="item in row.certificates"
                :key="`${row.id}-${item.id}-${item.certificate_group && item.certificate_group.id}`"
                class="cert-list-item"
              >
                <span
                  class="cert-list-item__icon"
                  :style="{ color: getGroupMeta(item.certificate_group).color, background: getGroupMeta(item.certificate_group).soft }"
                >
                  <feather-icon
                    :icon="getGroupMeta(item.certificate_group).icon"
                    size="14"
                  />
                </span>

                <div class="cert-list-item__text">
                  <span class="cert-list-item__en">{{ item.name.en || item.name.ar || 'Untitled certificate' }}</span>
                  <span
                    v-if="item.name.ar && item.name.en && item.name.ar !== item.name.en"
                    class="cert-list-item__ar"
                    dir="rtl"
                  >{{ item.name.ar }}</span>

                  <span
                    v-if="item.certificate_group && item.certificate_group.name"
                    class="cert-group-chip"
                    :style="{ color: getGroupMeta(item.certificate_group).color, background: getGroupMeta(item.certificate_group).soft }"
                  >
                    {{ item.certificate_group.name.en || item.certificate_group.name.ar }}
                  </span>
                </div>
              </div>
            </div>
            <div
              v-else
              class="no-certs"
            >
              <feather-icon
                icon="FileMinusIcon"
                size="14"
                class="mr-25"
              />
              No certificates linked to this category
            </div>
          </div>
        </div>
      </b-col>
    </b-row>

    <!-- ===================== Pagination ===================== -->
    <div
      v-if="!dataLoad && rows.length"
      class="mt-1"
    >
      <Pagination
        :page="page"
        :per-page="perPage"
        :total-rows="totalRows"
        @get-all-data="getAllData"
      />
    </div>
  </div>
</template>

<script>
/* eslint-disable camelcase */
import axios from 'axios'
import {
  BRow, BCol, BCard, BCardBody, BAvatar, BBadge,
  BInputGroup, BInputGroupPrepend, BInputGroupAppend, BFormInput,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import decryptData from '@/store/auth/decryptData'
import Pagination from '@/components/general-table/specific-utils/Pagination.vue'

const PRODUCT_ICON_RULES = [
  { keys: ['water'], icon: 'DropletIcon' },
  { keys: ['batter', 'cell'], icon: 'BatteryChargingIcon' },
  { keys: ['machin', 'tool', 'hand held'], icon: 'ToolIcon' },
  { keys: ['pressure', 'vessel'], icon: 'WindIcon' },
  { keys: ['oil', 'lub'], icon: 'ThermometerIcon' },
  { keys: ['paint'], icon: 'PenToolIcon' },
  { keys: ['quality', 'management system'], icon: 'ShieldIcon' },
  { keys: ['environmental'], icon: 'CloudIcon' },
  { keys: ['food', 'hazard', 'haccp'], icon: 'CoffeeIcon' },
  { keys: ['health', 'safety'], icon: 'HeartIcon' },
  { keys: ['manufactur'], icon: 'SettingsIcon' },
  { keys: ['conformity', 'assessment'], icon: 'CheckCircleIcon' },
  { keys: ['voltage', 'circuit', 'plug', 'socket', 'switch', 'cable', 'wire'], icon: 'ZapIcon' },
  { keys: ['laser'], icon: 'SunIcon' },
  { keys: ['tv', 'audio', 'video', 'it equipment'], icon: 'TvIcon' },
  { keys: ['light'], icon: 'ZapIcon' },
]

const PRODUCT_ICON_FALLBACK = ['PackageIcon', 'BoxIcon', 'ClipboardIcon', 'ArchiveIcon']

const PRODUCT_GRADIENTS = [
  'linear-gradient(135deg, #ff9f43, #ffbc7d)',
  'linear-gradient(135deg, #7367f0, #9e95f5)',
  'linear-gradient(135deg, #28c76f, #48da89)',
  'linear-gradient(135deg, #00cfe8, #1ce7ff)',
  'linear-gradient(135deg, #ea5455, #f97066)',
  'linear-gradient(135deg, #818cf8, #4f46e5)',
  'linear-gradient(135deg, #22d3ee, #0891b2)',
  'linear-gradient(135deg, #f6d365, #fda085)',
]

const GROUP_PALETTE = [
  { color: '#7367f0', soft: 'rgba(115, 103, 240, 0.12)', icon: 'TagIcon' },
  { color: '#28c76f', soft: 'rgba(40, 199, 111, 0.12)', icon: 'CheckCircleIcon' },
  { color: '#00cfe8', soft: 'rgba(0, 207, 232, 0.12)', icon: 'ShieldIcon' },
  { color: '#ff9f43', soft: 'rgba(255, 159, 67, 0.12)', icon: 'AwardIcon' },
  { color: '#ea5455', soft: 'rgba(234, 84, 85, 0.12)', icon: 'AlertCircleIcon' },
  { color: '#4f46e5', soft: 'rgba(79, 70, 229, 0.12)', icon: 'BookmarkIcon' },
]
const DEFAULT_GROUP_META = { color: '#82868b', soft: 'rgba(130, 134, 139, 0.12)', icon: 'TagIcon' }

export default {
  components: {
    Pagination,
    BRow,
    BCol,
    BCard,
    BCardBody,
    BAvatar,
    BBadge,
    BInputGroup,
    BInputGroupPrepend,
    BInputGroupAppend,
    BFormInput,
  },
  data() {
    return {
      apiUrl: 'product-categories-with-certificates',
      rows: [],
      totalRows: 0,
      page: 1,
      perPage: 20,
      dataLoad: false,
      searchQuery: '',
      stats: {
        totalProducts: 0,
        pageProducts: 0,
        totalCertificates: 0,
        totalGroups: 0,
      },
    }
  },
  mounted() {
    this.getAllData(this.page)
  },
  methods: {
    goBack() {
      this.$router.push({ name: 'product-and-certificate' })
    },

    async getAllData(page = 1) {
      this.page = page
      this.dataLoad = true
      try {
        const params = {
          skip: (page - 1) * this.perPage,
          ...(this.searchQuery ? { search: this.searchQuery } : {}),
        }

        const response = await axios.get(this.apiUrl, { params })

        if (response.status === 200) {
          const responseData = decryptData(response.data.payload)
          const firstKey = Object.keys(responseData?.data)[0]
          this.rows = responseData?.data[firstKey] || []
          this.totalRows = responseData.data.count || this.rows.length
          this.computeStats()
        }
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Failed to load product categories',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.dataLoad = false
      }
    },

    searchNow() {
      this.getAllData(1)
    },

    clearSearch() {
      this.searchQuery = ''
      this.getAllData(1)
    },

    computeStats() {
      const groups = new Set()
      let certificatesCount = 0

      this.rows.forEach(item => {
        if (Array.isArray(item.certificates)) {
          certificatesCount += item.certificates.length
          item.certificates.forEach(cert => {
            if (cert.certificate_group) groups.add(cert.certificate_group.id)
          })
        }
      })

      this.stats = {
        totalProducts: this.totalRows,
        pageProducts: this.rows.length,
        totalCertificates: certificatesCount,
        totalGroups: groups.size,
      }
    },

    getProductIcon(row) {
      const name = (row.name || '').toLowerCase()
      const rule = PRODUCT_ICON_RULES.find(({ keys }) => keys.some(key => name.includes(key)))

      if (rule) return rule.icon

      return PRODUCT_ICON_FALLBACK[row.id % PRODUCT_ICON_FALLBACK.length]
    },

    getProductGradient(row) {
      const key = `${row.id}`
      let hash = 0
      for (let i = 0; i < key.length; i += 1) {
        hash = (hash * 31 + key.charCodeAt(i)) % 1000000007
      }
      return PRODUCT_GRADIENTS[Math.abs(hash) % PRODUCT_GRADIENTS.length]
    },

    getGroupMeta(group) {
      if (!group) return DEFAULT_GROUP_META
      return GROUP_PALETTE[group.id % GROUP_PALETTE.length]
    },
  },
}
</script>

<style lang="scss" scoped src="./ProductCertificate.scss"></style>
